// IMPORTAZIONI E CONFIGURAZIONI
import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// LIBRERIE
import he from "he";

// MODULO CSS
import styles from './BlogDetail.module.css';

// HOOKS
import useGetArticoloById from '../../hooks/useGetArticoloById';
import { useGetAgencyDetails } from "../../hooks/useGetAgencyDetails";

// COMPONENTI
import { appConfig } from "../../config/appConfig";
import WidgetPartners from "../Widgets/WidgetPartners/WidgetPartners";
import WidgetContacts from "../Widgets/WidgetContacts/WidgetContacts";
import LoadingIndicator from "../Loaders/LoadingIndicator";
import NotFound from "../Error/NotFound";

// Helper per formattare date
const formatDate = (dateString: string | null, t: (key: string) => string): string => {
    if (!dateString) return t('blog.date.unavailable');
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            return t('blog.date.invalid');
        }
        return date.toLocaleDateString('it-IT', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    } catch (e) {
        console.error("Error formatting date:", dateString, e);
        return t('blog.date.invalid');
    }
};

interface BlogDetailProps {
    agencyId: number;
}

const BlogDetail: React.FC<BlogDetailProps> = ({ agencyId }) => {
    const { t } = useTranslation();
    const { articleId: articleIdParam } = useParams<{ articleId: string }>();
    const { agency, loading: agencyLoading, error: agencyError } = useGetAgencyDetails(agencyId);

    const allPartnersForWidget = appConfig.content.partners;

    const {
        articolo,
        isLoading: articleLoading,
        error: articleError,
    } = useGetArticoloById({
        id: Number(articleIdParam),
        agencyId: agencyId,
        enabled: !!articleIdParam
    });
    // --- Fine Fetch Articolo ---


    // --- Gestione Stati di Caricamento ed Errore ---
    if (agencyLoading || (!!articleIdParam && articleLoading)) return <LoadingIndicator />;

    if (agencyError) {
        console.error("Errore caricamento dati agenzia:", agencyError);
        return <NotFound message={`${t('blogPage.loadingError')}`} />;
    }

    if (!articolo) return <NotFound message={t('blogPage.detail.notFound')} />;

    if (!articleIdParam || articleError) {
        console.error("Errore caricamento articolo:", articleError, "ID Param:", articleIdParam);
        const message = articleError === "Articolo non trovato."
            ? t('blogPage.detail.notFound')
            : t('blogPage.detail.errorLoading');
        return <NotFound message={message} />;
    }


    // --- Fine Gestione Stati ---

    // --- Rendering del Contenuto dell'Articolo ---
    const imageUrl = articolo.foto ? `https://www.casagest24.it/${articolo.foto}` : '/images/blog/placeholder.jpg';
    // const currentLanguage = i18n.language.split('-')[0] || 'it';
    return (
        <section className={`${styles.blogDetailSection} space-ptb blog-detail-page`}>
            <div className="container-fluid">
                <div className="row justify-content-around">
                    {/* Colonna Contenuto Articolo */}
                    <div className="col-lg-12">
                        <div className={`${styles.blogPost} blog-post mb-3`}>
                            {/* Titolo */}
                            <div className={`${styles.titleContainer} blog-post-title mb-3`}>
                                <h4 className={styles.title} dangerouslySetInnerHTML={{ __html: he.decode(articolo.titolo) }} />
                            </div>

                            {/* Meta Info (Categoria, Data) */}
                            <div className="blog-post-details mb-3 pb-3 border-bottom">
                                <div className="d-flex justify-content-between align-items-center flex-wrap">
                                    {articolo.nomeCategoria && (
                                        <div className="blog-post-category mb-2 mb-sm-0">
                                            <Link to={`#`}>
                                                <i className="fas fa-folder-open mr-1"></i>
                                                <strong>{articolo.nomeCategoria}</strong>
                                            </Link>
                                        </div>
                                    )}
                                    <div className={`${styles.timeContainer} blog-post-time`}>
                                        <i className="far fa-clock mr-1"></i>
                                        {formatDate(articolo.dataPubblicazione, t)}
                                        {/* Potremmo mostrare anche modifyAt se rilevante */}
                                        {/* {articolo.modifyAt && ` (Mod: ${formatDate(articolo.modifyAt)})`} */}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-12 col-lg-8">
                        {/* Immagine Principale */}
                        {articolo.foto && (
                            <div className={`${styles.imageContainer} blog-post-image mb-4`}>
                                <img className={`${styles.image} img-fluid w-100`} src={imageUrl} alt={`${articolo.titolo || t('blogPage.article.imageAltFallback')}`} />
                            </div>
                        )}
                        {/* Testo Completo dell'Articolo */}
                        <div className={styles.contentContainer}>
                            <div dangerouslySetInnerHTML={{ __html: articolo.testo }} />
                        </div>

                        {/* (Opzionale) Footer Post: Tags, Condivisione Social, etc. */}
                        {/* <div className="blog-post-footer mt-4 pt-3 border-top"> ... </div> */}

                        {/* (Opzionale) Link per tornare alla lista blog */}
                        <div className={`${styles.backButtonContainer}`}>
                            <Link to="/blog" className={`${styles.backButton} btn btn-primary`}>
                                <i className="fas fa-arrow-left mr-2"></i>{t('blogPage.backToList')}
                            </Link>
                        </div>
                    </div>
                    <div className="col-12 col-lg-4 col-xl-3 mt-lg-4 sidebar">
                        <WidgetContacts
                            agencyDataAddress={agency?.address_details?.address ?? ''}
                            agencyDataCity={agency?.address_details?.city_name ?? ''}
                            agencyDataComuneCapoluogo={agency?.address_details?.city_name === agency?.address_details?.province_name}
                            agencyDataProvinciaSigla={agency?.address_details?.sigla ?? ''}
                            agencyDataEmail={appConfig.agency.agencyEmail}
                            agencyDataEmailShow={appConfig.ui.agencyWidget.showEmail}
                            agencyDataPhone1={agency?.phone1 ?? ''}
                            agencyDataPhone2={agency?.phone2 ?? ''}
                            agencyDataInternationalPrefix={appConfig.agency.intlPrefix ?? '+39'}
                            agencyDataMobile={agency?.mobile ?? ''}
                            agencyDataMobile2={agency?.mobile2 ?? ''}
                            agencySocialShow={appConfig.ui.agencyWidget.showSocialLinks}
                            agencyDataWhatsapp={appConfig.socialLinks.whatsapp}
                            agencyDataFacebook={appConfig.socialLinks.facebook}
                            agencyDataInstagram={appConfig.socialLinks.instagram}
                            agencyDataTwitter={appConfig.socialLinks.twitter}
                            agencyDataLinkedin={appConfig.socialLinks.linkedin}
                            agencyDataPinterest={appConfig.socialLinks.pinterest}
                            agencyDataYoutube={appConfig.socialLinks.youtube}
                        />
                        <WidgetPartners partners={allPartnersForWidget} />
                    </div>
                </div>
            </div>
        </section>
    );
};

export default BlogDetail;